import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { activeMentionToken, MENTION_RE_SRC } from "@/lib/mentions";
import { cn } from "@/lib/utils";

/** Split plain text into nodes, wrapping confirmed (dropdown-selected) @handles
 * in a teal span so a native input can show the tag styled while composing. */
function renderMentionNodes(text, confirmed) {
  if (!text) return null;
  const re = new RegExp(MENTION_RE_SRC, "g");
  const out = [];
  let last = 0, key = 0, m;
  while ((m = re.exec(text)) !== null) {
    const at = m.index + m[1].length; // index of '@'
    out.push(text.slice(last, at));
    const token = `@${m[2]}`;
    if (confirmed.has(m[2].toLowerCase())) {
      out.push(<span key={key++} className="so-mention-live">{token}</span>);
    } else {
      out.push(token);
    }
    last = m.index + m[0].length;
  }
  out.push(text.slice(last));
  return out;
}

/** Debounced mention suggestion search. `query=null` means the picker is closed. */
export function useMentionSearch() {
  const [query, setQuery] = useState(null);
  const [results, setResults] = useState([]);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    if (query === null || query === "") {
      timer.current = setTimeout(() => setResults([]), 0);
      return () => clearTimeout(timer.current);
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/handles/search?q=${encodeURIComponent(query)}&limit=8`);
        setResults(data || []);
      } catch (_) { setResults([]); }
    }, 140);
    return () => clearTimeout(timer.current);
  }, [query]);
  return { query, setQuery, results, setResults };
}

export function MentionSuggestionCard({ item, active, onSelect, onHover, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onSelect(); }}
      onMouseEnter={onHover}
      className={`flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition-colors ${active ? "bg-secondary" : "hover:bg-secondary/60"}`}
      data-testid={testId}
    >
      <Avatar className="h-8 w-8 shrink-0">
        {item.avatar_url && <AvatarImage src={fileUrl(item.avatar_url)} alt="" />}
        <AvatarFallback className="bg-teal-50 text-xs text-teal-700">{(item.display_name || "?")[0]}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-[560] text-foreground">{item.display_name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">@{item.handle}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[560] uppercase tracking-wide">{item.type_label}</span>
          {item.subtitle && <span className="truncate">{item.subtitle}</span>}
        </div>
      </div>
    </button>
  );
}

export function MentionDropdown({ items, activeIndex, onSelect, onHover, floating, coords }) {
  if (!items || !items.length) return null;
  const base = "z-[60] max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg";
  const style = floating ? { position: "absolute", left: coords?.left ?? 0, top: coords?.top ?? 0, width: 300 } : undefined;
  const cls = floating ? base : `absolute mt-1 w-full min-w-[260px] ${base}`;
  return (
    <div className={cls} style={style} data-testid="mention-dropdown">
      {items.map((it, i) => (
        <MentionSuggestionCard
          key={`${it.profile_type}-${it.profile_id}`}
          item={it}
          active={i === activeIndex}
          onSelect={() => onSelect(it)}
          onHover={() => onHover(i)}
          testId={`mention-option-${it.handle}`}
        />
      ))}
    </div>
  );
}

/** Drop-in Input/Textarea with @mention autocomplete. Inserts a plain `@handle` on
 * selection (never the display name). Single source of mention-input behavior for comments. */
export function MentionInput({ value, onChange, onKeyDown, multiline = false, testId, className, inputRef, knownHandles, ...rest }) {
  const ref = useRef(null);
  const backdropRef = useRef(null);
  // Merge the internal ref with an optional external ref (so an emoji picker can
  // insert at the caret of the underlying input/textarea).
  const setRefs = (node) => {
    ref.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) inputRef.current = node;
  };
  const tokenRef = useRef(null);
  const { query, setQuery, results, setResults } = useMentionSearch();
  const [activeIndex, setActiveIndex] = useState(0);
  // Confirmed (dropdown-selected) handles → teal in the backdrop. Seeded from
  // knownHandles so existing tags stay teal when editing a comment.
  const [confirmed, setConfirmed] = useState(() => new Set((knownHandles || []).map((h) => String(h).toLowerCase())));
  const open = query !== null && results.length > 0;

  useEffect(() => {
    if (!knownHandles || !knownHandles.length) return;
    setConfirmed((prev) => {
      let changed = false;
      const next = new Set(prev);
      knownHandles.forEach((h) => { const k = String(h).toLowerCase(); if (!next.has(k)) { next.add(k); changed = true; } });
      return changed ? next : prev;
    });
  }, [knownHandles]);

  const detect = (el) => {
    const caret = el.selectionStart ?? el.value.length;
    const tok = activeMentionToken(el.value, caret);
    if (tok && tok.query.length >= 1) { tokenRef.current = { ...tok, caret }; setQuery(tok.query); setActiveIndex(0); }
    else { tokenRef.current = null; setQuery(null); setResults([]); }
  };

  const syncScroll = () => {
    const el = ref.current, b = backdropRef.current;
    if (el && b) { b.scrollTop = el.scrollTop; b.scrollLeft = el.scrollLeft; }
  };

  const handleChange = (e) => { onChange(e.target.value); detect(e.target); requestAnimationFrame(syncScroll); };

  const select = (item) => {
    if (!item) return;
    const el = ref.current;
    const tok = tokenRef.current;
    const caret = el ? (el.selectionStart ?? value.length) : value.length;
    const start = tok ? tok.start : caret;
    const before = value.slice(0, start);
    const after = value.slice(caret);
    const insert = `@${item.handle} `;
    setConfirmed((prev) => new Set(prev).add(String(item.handle).toLowerCase()));
    onChange(before + insert + after);
    setQuery(null); setResults([]); tokenRef.current = null;
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = (before + insert).length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (open) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => (i + 1) % results.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => (i - 1 + results.length) % results.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); select(results[activeIndex]); return; }
      if (e.key === "Escape") { e.preventDefault(); setQuery(null); setResults([]); return; }
    }
    if (onKeyDown) onKeyDown(e);
  };

  const Comp = multiline ? Textarea : Input;
  // The backdrop must share the field's exact box model so the styled text sits
  // pixel-perfectly under the (transparent) field text. Field bg moves to the
  // backdrop; the field keeps its border/ring on top.
  const boxCls = multiline ? "px-3 py-2 text-base md:text-sm" : "px-3 py-1 text-base md:text-sm";
  const wrapCls = multiline ? "whitespace-pre-wrap break-words" : "whitespace-pre";
  return (
    <div className="relative w-full">
      <div
        ref={backdropRef}
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 z-0 overflow-hidden border border-transparent bg-[hsl(var(--card)/0.6)] text-foreground backdrop-blur-sm",
          boxCls, wrapCls, className
        )}
        data-testid={testId ? `${testId}-backdrop` : undefined}
      >
        {renderMentionNodes(value, confirmed)}
        {"\u200b"}
      </div>
      <Comp
        ref={setRefs}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onScroll={syncScroll}
        onBlur={() => setTimeout(() => { setQuery(null); setResults([]); }, 150)}
        data-testid={testId}
        className={cn("relative z-[1] bg-transparent text-transparent caret-[hsl(var(--foreground))] backdrop-blur-none", className)}
        {...rest}
      />
      {open && (
        <MentionDropdown items={results} activeIndex={activeIndex} onSelect={select} onHover={setActiveIndex} />
      )}
    </div>
  );
}
